<template>
  <div>
    <v-card :class="eye + 'Background'" class="mb-4" flat>
      <v-card-title
        class="d-flex flex-row align-center pa-2"
        :class="eye + 'HeaderBackground'"
      >
        <div class="title">{{ eye | eyeText }} Eye</div>
        <v-spacer></v-spacer>
        <v-fade-transition>
          <v-btn
            v-if="dirty"
            x-small
            rounded
            depressed
            color="#FFF"
            class="mx-1"
            @click="clearForm()"
            >Clear Form</v-btn
          >
        </v-fade-transition>
      </v-card-title>

      <v-card-text class="pt-4 darkerCardText">
        <table>
          <tr>
            <td>
              <v-text-field
                label="GP Power"
                class="mr-2 wInput"
                v-model="inputs['pwr']"
                @keyup.enter="formatDiopterInput('pwr')"
                @blur="formatDiopterInput('pwr')"
                dense
                outlined
              >
              </v-text-field>
            </td>
            <td>
              <v-text-field
                label="GP Base Curve"
                type="number"
                step=".01"
                dense
                outlined
                v-model="inputs['bc']"
                @keyup.enter="formatKInput('bc')"
                @blur="formatKInput('bc')"
                :messages="
                  objects.bc
                    ? [prettyKstringDandMM(objects.bc.k1, 1)]
                    : ['Diopters or mm']
                "
                :error-messages="invalidBC ? ['Invalid K value'] : []"
                class="wInput"
              ></v-text-field>
            </td>
          </tr>
          <tr>
            <td>
              <v-text-field
                label="Over Refraction"
                class="mr-2 wInput"
                v-model="inputs['or']"
                @keyup.enter="formatDiopterInput('or')"
                @blur="formatDiopterInput('or')"
                dense
                outlined
                :messages="
                  needToVertex
                    ? ['Vertexed: ' + numToDiopterString(vertexedOR.sph)]
                    : []
                "
              ></v-text-field>
            </td>
            <td>
              <v-text-field
                label="Desired BC Change"
                class="wInput"
                v-model="inputs['bcChange']"
                @keyup.enter="formatDiopterInput('bcChange')"
                @blur="formatDiopterInput('bcChange')"
                dense
                outlined
                hide-details="auto"
                :messages="['(Diopters only)']"
              ></v-text-field>
              <v-radio-group
                v-model="inputs['bcChangeDir']"
                row
                dense
                class="mt-0"
              >
                <v-radio label="Flatter" :value="-1"></v-radio>
                <v-radio label="Steeper" :value="1"></v-radio>
              </v-radio-group>
            </td>
          </tr>
          <tr v-if="result">
            <td colspan="2" class="pt-2 pb-4">
              <hr />
            </td>
          </tr>
          <tr v-if="result">
            <td class="overline text-decoration-underline">New Power</td>
            <td class="overline text-decoration-underline">New BC</td>
          </tr>
          <tr v-if="result">
            <td class="font-weight-bold">
              {{ numToDiopterString(result.pwr, 1) }}
            </td>
            <td class="font-weight-bold">
              {{ this.prettyKstringDandMM(result.bc, 1) }}
            </td>
          </tr>
        </table>
        <v-fade-transition>
          <div class="caption mt-4 pa-2 discussionDiv" v-if="showDiscussion">
            Discussion
            <hr class="mb-2" />

            <div v-if="this.objects.bcChange && this.objects.bcChange.sph > 0">
              <p>
                {{ this.inputs.bcChangeDir > 0 ? "Steepening" : "Flattening" }}
                the base curve by
                {{ numToDiopterString(this.objects.bcChange.sph, D) }} will
                induce
                {{ this.inputs.bcChangeDir > 0 ? "plus" : "minus" }} power into
                the "tear lens".
              </p>
              <p>
                To maintain the same equivalent power, we change it by
                {{
                  numToDiopterString(
                    this.objects.bcChange.sph * -this.inputs.bcChangeDir,
                    1
                  )
                }}
                ({{
                  this.inputs.bcChangeDir > 0
                    ? "SAM = Steep add Minus"
                    : "FAP = Flat add Plus"
                }}), which would make the adjusted power
                {{
                  numToDiopterString(
                    this.objects.pwr.sph +
                      this.objects.bcChange.sph * -this.inputs.bcChangeDir,
                    1
                  )
                }}.
              </p>
            </div>
            <p v-if="this.objects.or && this.objects.or.sph != 0">
              To address the overrefraction we change the power by
              {{ numToDiopterString(this.objects.or.sph, 1) }}, resulting a new
              lens power of {{ numToDiopterString(result.pwr, 1) }}.
            </p>
          </div>
        </v-fade-transition>
        <!-- {{ bcChangeText }} -->
        <!-- <v-text-field
          label="BC Change Direction"
          class="mb-1"
          v-model="inputs['bcChangeDir']"
          type="number"
          dense
          outlined
        ></v-text-field> -->
        <div class="red--text" v-if="invalidBC">
          Please enter a base curve between 30.00-70.00D or 4.8-11mm.
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import RxMethods from "@/assets/js/cls/RxMethods";

export default {
  name: "SamfapEye",
  mixins: [RxMethods],
  props: {
    eye: null,
    round: null,
  },

  data: function () {
    return {
      inputs: {
        pwr: null,
        bc: null,
        or: null,
        bcChange: null,
        bcChangeDir: -1,
      },
      objects: {
        pwr: null,
        bc: null,
        or: null,
      },
      invalidBC: null,
    };
  },
  methods: {
    formatDiopterInput(field) {
      if (this.objects[field]) {
        let sign = field == "bcChange" ? null : 1;
        let unit = field == "bcChange" ? "" : " D";
        this.inputs[field] =
          this.numToDiopterString(this.objects[field].sph, sign) + unit;
      }
    },
    formatKInput(field) {
      if (this.objects[field])
        this.inputs[field] = this.prettyKstring(this.objects[field].k1);
      this.invalidBC = !this.checkValidKvalue(parseFloat(this.inputs[field]));
    },
    clearForm() {
      this.inputs = {
        pwr: "",
        bc: "",
        or: "0",
        bcChange: "",
        bcChangeDir: -1,
      };
    },

    // formatBcChange() {
    //   this.inputs["bcChange"] = this.inputs["bcChange"].toFixed(2);
    // },
  },
  computed: {
    vertexedOR: function () {
      return this.objects.or && this.needToVertex
        ? this.diffVertex(this.objects.or)
        : this.objects.or;
    },
    needToVertex: function () {
      return this.objects.or && Math.abs(this.objects.or.sph) > 3.9;
    },

    result: function () {
      if (!this.objects.pwr || !this.objects.bc) return null;
      let result = { pwr: "", bc: "" };
      //   console.log(this.objects.bcChange, this.inputs.bcChangeDir);
      let bcChange = this.objects.bcChange
        ? parseFloat(this.objects.bcChange.sph) * this.inputs.bcChangeDir
        : 0;
      let bcNum = this.kValueD(this.objects.bc.k1) + bcChange;
      result.bc = bcNum;

      let pwrChange = this.vertexedOR ? this.vertexedOR.sph : 0;

      result.pwr = this.objects.pwr.sph + pwrChange - bcChange;
      return result;
    },
    dirty: function () {
      return (
        this.inputs.pwr ||
        this.inputs.bc ||
        (this.inputs.or && this.inputs.or != "0") ||
        this.inputs.bcChange
      );
    },
    showDiscussion: function () {
      return this.result && (this.objects.bcChange || this.objects.or);
    },
  },
  watch: {
    inputs: {
      handler: function (val) {
        this.objects.pwr = this.parseRxString(val.pwr);
        this.objects.bc = this.parseKstring(val.bc);
        this.objects.or = this.parseRxString(val.or);
        this.objects.bcChange = this.parseRxString(val.bcChange);
        if (this.objects.bcChange?.sph < 0) this.objects.bcChange.sph *= -1;
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
td,
th {
  vertical-align: top;
  text-align: left;
}
.discussionDiv {
  background-color: #ffffff96;
}

.wInput >>> .v-input__slot {
  background-color: #fff !important;
}

.wInput >>> .v-text-field__details {
  color: rgba(166, 44, 44, 0.84) !important;
}
</style>
